import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Modal, ModalBody, ModalFooter,
  Button, Spinner, Row, Col, FormGroup, Label, Input,
  Carousel, CarouselItem, CarouselControl, CarouselIndicators, CarouselCaption,
} from 'reactstrap';
import Select from 'react-select';
import FeatherIcon from 'feather-icons-react';
import { Tooltip } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import {
  requestInspectionUploadUrl,
  confirmInspectionUpload,
  getInspectionAttachmentSignedUrls,
} from './FormikHelpers/Api';
import { defaultInspectionItem } from './FormikHelpers/InitialValues';
import './inspection-tracker.scss';

const MAX_FILES_PER_ITEM = 5;
const MAX_FILE_SIZE = 120 * 1024 * 1024;
const MAX_ITEMS = 20;
const ALLOWED_FILE_TYPES = 'image/jpeg,image/jpg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/webm,video/x-msvideo,video/x-matroska';

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const buildSelectStyles = () => ({
  menuPortal: (b) => ({ ...b, zIndex: 99999 }),
  container: (b) => ({ ...b, width: '100%' }),
  control: (b, { isFocused }) => ({
    ...b,
    minHeight: 36,
    fontSize: '0.82rem',
    borderRadius: 8,
    borderColor: isFocused ? '#1A6B4A' : '#D4E8DC',
    borderWidth: '1.5px',
    boxShadow: isFocused ? '0 0 0 3px #E8F5EE' : 'none',
    '&:hover': { borderColor: '#1A6B4A' },
    backgroundColor: '#F7FAF8',
  }),
  valueContainer: (b) => ({ ...b, padding: '0 12px' }),
  dropdownIndicator: (b) => ({ ...b, padding: '0 8px', color: '#6B8F7C' }),
  indicatorSeparator: () => ({ display: 'none' }),
  menu: (b) => ({ ...b, fontSize: '0.82rem', borderRadius: 8, zIndex: 9999, boxShadow: '0 12px 40px rgba(26,107,74,0.12)', border: '1px solid #D4E8DC' }),
  option: (b, { isSelected, isFocused }) => ({
    ...b,
    backgroundColor: isSelected ? '#1A6B4A' : isFocused ? '#E8F5EE' : 'white',
    color: isSelected ? 'white' : '#1A2D24',
    fontWeight: isSelected ? 600 : 400,
    padding: '8px 12px',
    borderRadius: 5,
  }),
  singleValue: (b) => ({ ...b, color: '#1A2D24', fontWeight: 600 }),
  placeholder: (b) => ({ ...b, color: '#6B8F7C' }),
});

const MediaCarouselModal = ({ isOpen, toggle, items, initialIndex = 0 }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [animating, setAnimating] = useState(false);

  useEffect(() => { if (isOpen) setActiveIndex(initialIndex); }, [isOpen, initialIndex]);

  const next = () => { if (!animating) setActiveIndex((i) => (i === items.length - 1 ? 0 : i + 1)); };
  const prev = () => { if (!animating) setActiveIndex((i) => (i === 0 ? items.length - 1 : i - 1)); };

  if (!isOpen || !items?.length) return null;

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="xl" className="it-carousel-modal" style={{ zIndex: 99999 }}>
      <div className="it-carousel-modal__header">
        <div className="it-carousel-modal__header-left">
          <div className="it-carousel-modal__icon"><FeatherIcon icon="film" size={15} color="white" /></div>
          <span className="it-carousel-modal__title">Media Preview</span>
          <span className="it-carousel-modal__count">{items.length} item{items.length !== 1 ? 's' : ''}</span>
        </div>
        <button type="button" className="it-icon-btn" onClick={toggle}><FeatherIcon icon="x" size={14} color="white" /></button>
      </div>
      <ModalBody className="p-0" style={{ backgroundColor: '#0A1A12' }}>
        <Carousel activeIndex={activeIndex} next={next} previous={prev} pause={false} interval={false}>
          <CarouselIndicators items={items} activeIndex={activeIndex} onClickHandler={(i) => { if (!animating) setActiveIndex(i); }} />
          {items.map((item, idx) => (
            <CarouselItem onExiting={() => setAnimating(true)} onExited={() => setAnimating(false)} key={idx}>
              <div style={{ textAlign: 'center', minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0A1A12' }}>
                {item.type === 'video'
                  ? <video src={item.src} controls autoPlay style={{ maxHeight: '70vh', width: '100%', objectFit: 'contain' }} />
                  : <img src={item.src} alt={item.caption} style={{ maxHeight: '70vh', width: '100%', objectFit: 'contain' }} />}
              </div>
              <CarouselCaption captionText={item.caption} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} />
            </CarouselItem>
          ))}
          <CarouselControl direction="prev" directionText="Prev" onClickHandler={prev} />
          <CarouselControl direction="next" directionText="Next" onClickHandler={next} />
        </Carousel>
      </ModalBody>
      <div className="it-carousel-modal__footer">
        <button type="button" className="it-btn it-btn--ghost-light" onClick={toggle}>Close</button>
      </div>
    </Modal>
  );
};

const uploadAttachmentFiles = async (inspectionId, inspectionIndex, pendingFiles, existingKeys = []) => {
  const keys = [];
  for (const { file, name } of pendingFiles) {
    try {
      const { presignedUrl, key } = await requestInspectionUploadUrl(inspectionId, name, file.type, 'General');
      await fetch(presignedUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      await confirmInspectionUpload(inspectionId, key, inspectionIndex);
      keys.push(key);
    } catch (err) {
      console.error(`Upload failed for file ${name}:`, err);
    }
  }
  return [...existingKeys, ...keys];
};

const AttachmentUploader = ({ itemIndex, existingAttachments = [], onAttachmentsChange, inspectionId, isEditMode, readOnly }) => {
  const [pendingFiles, setPendingFiles] = useState([]);
  const [signedUrls, setSignedUrls] = useState({});
  const [uploading, setUploading] = useState(false);
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselStart, setCarouselStart] = useState(0);
  const fetchedRef = useRef(new Set());

  useEffect(() => {
    const missing = existingAttachments.filter((k) => !signedUrls[k] && !fetchedRef.current.has(k));
    if (!missing.length) return;
    missing.forEach((k) => fetchedRef.current.add(k));
    getInspectionAttachmentSignedUrls(missing)
      .then((urls) => setSignedUrls((prev) => ({ ...prev, ...urls })))
      .catch(() => toast.error('Some previews failed to load'));
  }, [existingAttachments, signedUrls]);

  useEffect(() => () => pendingFiles.forEach((f) => f.preview && URL.revokeObjectURL(f.preview)), [pendingFiles]);

  useEffect(() => {
    if (pendingFiles.length > 0 && inspectionId && !uploading && isEditMode && !readOnly) {
      (async () => {
        setUploading(true);
        const updatedKeys = await uploadAttachmentFiles(inspectionId, itemIndex, pendingFiles, existingAttachments);
        setPendingFiles([]);
        setUploading(false);
        onAttachmentsChange(updatedKeys);
        if (updatedKeys.length > existingAttachments.length) {
          toast.success(`${updatedKeys.length - existingAttachments.length} file(s) uploaded`);
        }
      })();
    }
  }, [pendingFiles.length, inspectionId, uploading, isEditMode, readOnly, itemIndex, existingAttachments, onAttachmentsChange]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (accepted, rejected) => {
      if (readOnly) return;
      rejected.forEach(({ file, errors }) => {
        errors.forEach((e) => {
          if (e.code === 'file-too-large') toast.error(`${file.name} exceeds 120 MB`);
          if (e.code === 'file-invalid-type') toast.error(`${file.name}: unsupported type`);
        });
      });
      const rem = MAX_FILES_PER_ITEM - (pendingFiles.length + existingAttachments.length);
      const toAdd = accepted.slice(0, rem);
      if (accepted.length > rem) toast.warn(`Max ${MAX_FILES_PER_ITEM} files per item`);
      if (!toAdd.length) return;
      setPendingFiles((prev) => [...prev, ...toAdd.map((file) => ({ file, name: file.name, type: file.type, preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null }))]);
    },
    accept: ALLOWED_FILE_TYPES,
    multiple: true,
    maxSize: MAX_FILE_SIZE,
    disabled: readOnly,
  });

  const removePending = (i, e) => {
    if (readOnly) return;
    e.stopPropagation();
    setPendingFiles((p) => { const f = p[i]; if (f?.preview) URL.revokeObjectURL(f.preview); return p.filter((_, idx) => idx !== i); });
  };

  const removeUploaded = async (i, e) => {
    if (readOnly) return;
    e.stopPropagation();
    const r = await Swal.fire({ title: 'Remove attachment?', icon: 'warning', showCancelButton: true, confirmButtonColor: '#DC2626', confirmButtonText: 'Remove', width: 340 });
    if (r.isConfirmed) onAttachmentsChange(existingAttachments.filter((_, idx) => idx !== i));
  };

  const total = existingAttachments.length + pendingFiles.length;
  const carouselItems = useMemo(() => [
    ...existingAttachments.map((k, i) => ({ src: signedUrls[k] || '', caption: `Attachment ${i + 1}`, type: /\.(mp4|mov|webm|avi|mkv)$/i.test(k) ? 'video' : 'image' })),
    ...pendingFiles.map((f, i) => ({ src: f.preview || '', caption: `Pending ${i + 1}`, type: f.type.startsWith('video/') ? 'video' : 'image' })),
  ], [existingAttachments, pendingFiles, signedUrls]);

  return (
    <div className="it-attachment" onClick={(e) => e.stopPropagation()}>
      {total > 0 && (
        <button type="button" className="it-attachment__view-btn mb-1" onClick={() => { setCarouselStart(0); setCarouselOpen(true); }}>
          <FeatherIcon icon="image" size={10} /> {total} file{total !== 1 ? 's' : ''}
        </button>
      )}

      {!readOnly && (
        <div {...getRootProps()} className={`it-attachment__dropzone${isDragActive ? ' it-attachment__dropzone--active' : ''}`}>
          <input {...getInputProps()} />
          <div className="it-attachment__dropzone-inner">
            <FeatherIcon icon="upload-cloud" size={14} color={isDragActive ? '#1A6B4A' : '#6B8F7C'} />
            <span className="it-attachment__dropzone-label">
              {isDragActive ? 'Drop files here' : `Click or drag (${total}/${MAX_FILES_PER_ITEM})`}
            </span>
          </div>
        </div>
      )}

      {(existingAttachments.length > 0 || pendingFiles.length > 0) && (
        <div className="it-attachment__thumbs">
          {existingAttachments.map((k, i) => {
            const url = signedUrls[k];
            const isVid = /\.(mp4|mov|webm|avi|mkv)$/i.test(k);
            return (
              <div key={i} className="it-attachment__thumb" onClick={() => { setCarouselStart(i); setCarouselOpen(true); }}>
                {url ? (isVid ? <div className="it-attachment__thumb-video"><FeatherIcon icon="play" size={14} color="white" /></div> : <img src={url} alt="" className="it-attachment__thumb-img" />) : <div className="it-attachment__thumb-loading"><Spinner size="sm" /></div>}
                {!readOnly && isEditMode && (
                  <button type="button" className="it-attachment__remove-btn" onClick={(e) => removeUploaded(i, e)}>
                    <FeatherIcon icon="x" size={8} color="white" />
                  </button>
                )}
              </div>
            );
          })}
          {!readOnly && pendingFiles.map((f, i) => (
            <div key={`p${i}`} className="it-attachment__thumb it-attachment__thumb--pending">
              {f.type.startsWith('image/') ? <img src={f.preview} alt="" className="it-attachment__thumb-img" /> : <div className="it-attachment__thumb-video"><FeatherIcon icon="video" size={14} color="white" /></div>}
              <button type="button" className="it-attachment__remove-btn" onClick={(e) => removePending(i, e)}>
                <FeatherIcon icon="x" size={8} color="white" />
              </button>
            </div>
          ))}
        </div>
      )}

      {uploading && <div className="it-attachment__uploading"><Spinner size="sm" /> Uploading…</div>}

      {readOnly && total === 0 && (
        <span style={{ fontSize: '0.78rem', color: '#9BB5A8', fontStyle: 'italic' }}>No attachments</span>
      )}

      <MediaCarouselModal isOpen={carouselOpen} toggle={() => setCarouselOpen(false)} items={carouselItems} initialIndex={carouselStart} />
    </div>
  );
};

// readOnly = non-company users can only toggle completion, add notes, and upload attachments
const CompletedToggle = ({ item, onChange, readOnly }) => {
  const isCompleted = !!item.completed;

  const toggle = () => {
    if (readOnly) return;
    if (isCompleted) {
      onChange({ ...item, completed: null });
    } else {
      const today = new Date().toISOString().split('T')[0];
      onChange({ ...item, completed: { completedDate: today, notes: '', attachments: item.completed?.attachments || [], contractorInvoice: '' } });
    }
  };

  return (
    <div className="it-completed-section">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: isCompleted ? 10 : 0 }}>
        <div
          onClick={toggle}
          style={{ width: 36, height: 20, borderRadius: 10, background: isCompleted ? '#1A6B4A' : '#D4E8DC', position: 'relative', transition: 'background 0.2s', flexShrink: 0, cursor: readOnly ? 'not-allowed' : 'pointer', opacity: readOnly ? 0.6 : 1 }}
        >
          <div style={{ position: 'absolute', top: 3, left: isCompleted ? 18 : 3, width: 14, height: 14, borderRadius: '50%', background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s' }} />
        </div>
        <span style={{ fontSize: '0.82rem', fontWeight: 600, color: isCompleted ? '#1A6B4A' : '#6B8F7C' }}>
          {isCompleted ? 'Completed' : 'Mark Complete'}
        </span>
        {readOnly && <span style={{ fontSize: '0.70rem', color: '#9BB5A8', fontStyle: 'italic' }}>(view only)</span>}
      </div>

      {isCompleted && (
        <div>
          <FormGroup className="mb-2">
            <Label style={{ fontSize: '0.78rem', color: '#6B8F7C', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}>
              Completion Date
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.68rem', color: '#1A6B4A', background: '#E8F5EE', border: '1px solid #A7F3D0', borderRadius: 4, padding: '1px 6px', fontWeight: 600 }}>
                <FeatherIcon icon="lock" size={9} color="#1A6B4A" />Auto-set
              </span>
            </Label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 7, background: '#F0FDF4', border: '1px solid #A7F3D0', fontSize: '0.82rem', fontWeight: 700, color: '#1A6B4A' }}>
              <FeatherIcon icon="calendar" size={13} color="#1A6B4A" />
              {item.completed?.completedDate ? fmtDate(item.completed.completedDate) : fmtDate(new Date().toISOString().split('T')[0])}
            </div>
          </FormGroup>

          <FormGroup className="mb-2">
            <Label style={{ fontSize: '0.78rem', color: '#6B8F7C', fontWeight: 600 }}>Completion Notes</Label>
            <Input
              type="textarea"
              bsSize="sm"
              rows={2}
              value={item.completed?.notes || ''}
              onChange={(e) => !readOnly && onChange({ ...item, completed: { ...(item.completed || {}), notes: e.target.value } })}
              placeholder={readOnly ? '—' : 'Notes about this completion...'}
              readOnly={readOnly}
              style={{ borderColor: '#D4E8DC', borderRadius: 7, fontSize: '0.82rem', resize: 'vertical', backgroundColor: readOnly ? '#f8f9fa' : undefined }}
            />
          </FormGroup>
        </div>
      )}
    </div>
  );
};

const ReminderDaysInput = ({ item, onChange }) => {
  const frequencyDays = item.frequencyDays;
  const days = item.reminderDays;
  const hasCompletedDate = !!item.completed?.completedDate;
  const hasDueDate = !!item.dueDate;
  const canShowNextDue = hasCompletedDate && !!frequencyDays;

  const nextDueDate = useMemo(() => {
    if (!canShowNextDue) return null;
    const d = new Date(item.completed.completedDate);
    d.setUTCDate(d.getUTCDate() + Number(frequencyDays));
    return d;
  }, [canShowNextDue, frequencyDays, item.completed?.completedDate]);

  const reminderDate = useMemo(() => {
    if (!days || days < 1) return null;
    const base = nextDueDate ? new Date(nextDueDate) : hasDueDate ? new Date(item.dueDate) : null;
    if (!base) return null;
    base.setUTCDate(base.getUTCDate() - Number(days));
    return base;
  }, [nextDueDate, hasDueDate, item.dueDate, days]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <FeatherIcon icon="bell" size={13} color="#D97706" />
        <Label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#6B8F7C', margin: 0 }}>Reminder Days</Label>
        <Tooltip arrow title="Days before the due date when reminder emails start. Alerts repeat every 2 days until the inspection is completed again." placement="top">
          <span style={{ cursor: 'help', color: '#9BB5A8', fontSize: '0.75rem' }}>ⓘ</span>
        </Tooltip>
      </div>

      {nextDueDate && (
        <div style={{ marginBottom: 6, padding: '3px 9px', borderRadius: 6, background: '#E8F5EE', border: '1px solid #A7F3D0', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <FeatherIcon icon="calendar" size={10} color="#1A6B4A" />
          <span style={{ fontSize: '0.72rem', color: '#1A6B4A', fontWeight: 600 }}>Next Due: {fmtDate(nextDueDate)}</span>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Input
          type="number" bsSize="sm" value={days || ''} min="1" max="365"
          onChange={(e) => { const v = e.target.value; onChange({ ...item, reminderDays: v === '' ? undefined : Math.max(1, Math.min(365, Math.round(Number(v)))) }); }}
          placeholder="Days before due, e.g. 5"
          style={{ borderColor: '#D4E8DC', borderRadius: 7, fontSize: '0.82rem', width: 130 }}
        />
        {days && (
          <button type="button" style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }} onClick={() => onChange({ ...item, reminderDays: undefined })}>
            <FeatherIcon icon="x-circle" size={15} color="#DC2626" />
          </button>
        )}
      </div>

      {reminderDate && (
        <div style={{ marginTop: 5, padding: '3px 9px', borderRadius: 6, background: '#FFFBEB', border: '1px solid #FDE68A', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <FeatherIcon icon="clock" size={10} color="#D97706" />
          <span style={{ fontSize: '0.72rem', color: '#92400E', fontWeight: 500 }}>First alert: {fmtDate(reminderDate)} · then every 2 days</span>
        </div>
      )}

      {!hasDueDate && !hasCompletedDate && (
        <span style={{ fontSize: '0.72rem', color: '#C4CFC9', fontStyle: 'italic', marginTop: 4, display: 'block' }}>Set a due date to preview reminder timing</span>
      )}

      {item.reminderSentAt && (
        <div style={{ marginTop: 5, padding: '3px 9px', borderRadius: 6, background: '#E8F5EE', border: '1px solid #A7F3D0', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <FeatherIcon icon="check-circle" size={10} color="#1A6B4A" />
          <span style={{ fontSize: '0.72rem', color: '#1A6B4A', fontWeight: 500 }}>Last sent {fmtDate(item.reminderSentAt)}</span>
        </div>
      )}
    </div>
  );
};

// ── Field-level read-only notice ──
const ReadOnlyBanner = () => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 8, padding: '8px 14px', marginBottom: 16 }}>
    <FeatherIcon icon="eye" size={14} color="#C2410C" />
    <span style={{ fontSize: '0.80rem', color: '#C2410C', fontWeight: 600 }}>
      View mode — you can update completion status, notes, and attachments only.
    </span>
  </div>
);

const InspectionItemCard = ({ item, index, inspectionId, isEditMode, typeOptions, frequencyOptions, onChange, onRemove, canRemove, isCompanyRole }) => {
  const selectStyles = buildSelectStyles();
  const typeOption = typeOptions.find((o) => o.value === item.inspectionTypeId) || null;
  const freqOption = frequencyOptions.find((o) => o.value === item.frequencyId) || null;
  const isOverdue = !item.completed && item.dueDate && new Date(item.dueDate) < new Date();

  // Non-company users: only completion fields are editable
  const fieldReadOnly = !isCompanyRole;

  const handleField = (field, value) => {
    if (fieldReadOnly) return;
    onChange(index, { ...item, [field]: value });
  };

  const nextDueDate = useMemo(() => {
    if (!item.completed?.completedDate || !item.frequencyDays) return null;
    const d = new Date(item.completed.completedDate);
    d.setUTCDate(d.getUTCDate() + Number(item.frequencyDays));
    return d;
  }, [item.completed?.completedDate, item.frequencyDays]);

  return (
    <div style={{ border: `1.5px solid ${isOverdue ? '#FECDD3' : '#D4E8DC'}`, borderRadius: 10, padding: '14px 16px', marginBottom: 12, background: isOverdue ? '#FEF2F2' : item.completed ? '#F0FDF4' : '#F7FAF8', position: 'relative' }}>
      {/* Card header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#E8F5EE', border: '1px solid #D4E8DC', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', fontWeight: 700, color: '#1A6B4A' }}>{index + 1}</div>
          {item.completed && <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#1A6B4A', background: '#E8F5EE', border: '1px solid #A7F3D0', borderRadius: 5, padding: '1px 8px' }}>✓ Completed</span>}
          {isOverdue && <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#DC2626', background: '#FEF2F2', border: '1px solid #FECDD3', borderRadius: 5, padding: '1px 8px' }}>⚠ Overdue</span>}
          {nextDueDate && <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#1A6B4A', background: '#E8F5EE', border: '1px solid #A7F3D0', borderRadius: 5, padding: '1px 8px' }}>📅 Next Due: {fmtDate(nextDueDate)}</span>}
        </div>
        {canRemove && isCompanyRole && (
          <button type="button" className="it-dbtn" onClick={() => onRemove(index)} title="Remove item">
            <FeatherIcon icon="trash-2" size={13} color="#DC2626" />
          </button>
        )}
      </div>

      {/* Core fields — read-only for non-company */}
      <Row className="g-3">
        <Col md={6}>
          <FormGroup>
            <Label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#4A7060' }}>Inspection Type *</Label>
            {fieldReadOnly ? (
              <div className="form-control form-control-sm" style={{ backgroundColor: '#f8f9fa', color: '#495057', fontSize: '0.82rem', borderColor: '#D4E8DC', borderRadius: 8 }}>
                {item.inspectionTypeName || '—'}
              </div>
            ) : (
              <Select
                options={typeOptions} value={typeOption}
                onChange={(o) => onChange(index, { ...item, inspectionTypeId: o?.value || '', inspectionTypeName: o?.label || '', contractor: item.contractor || (typeOptions.find((t) => t.value === o?.value)?.defaultContractor || '') })}
                styles={selectStyles} menuPortalTarget={document.body} placeholder="Select inspection type..." isClearable
              />
            )}
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#4A7060' }}>Frequency *</Label>
            {fieldReadOnly ? (
              <div className="form-control form-control-sm" style={{ backgroundColor: '#f8f9fa', color: '#495057', fontSize: '0.82rem', borderColor: '#D4E8DC', borderRadius: 8 }}>
                {item.frequencyName || '—'}
              </div>
            ) : (
              <Select
                options={frequencyOptions} value={freqOption}
                onChange={(o) => onChange(index, { ...item, frequencyId: o?.value || '', frequencyName: o?.label || '', frequencyDays: o?.days })}
                styles={selectStyles} menuPortalTarget={document.body} placeholder="Select frequency..." isClearable
              />
            )}
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#4A7060' }}>Contractor *</Label>
            <Input type="text" bsSize="sm" value={item.contractor || ''} onChange={(e) => handleField('contractor', e.target.value)} placeholder={fieldReadOnly ? '—' : 'Contractor name...'} readOnly={fieldReadOnly}
              style={{ borderColor: '#D4E8DC', borderRadius: 7, fontSize: '0.82rem', backgroundColor: fieldReadOnly ? '#f8f9fa' : undefined }} />
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#4A7060' }}>Due Date *</Label>
            <Input type="date" bsSize="sm" value={item.dueDate ? new Date(item.dueDate).toISOString().split('T')[0] : ''} onChange={(e) => handleField('dueDate', e.target.value)} readOnly={fieldReadOnly}
              style={{ borderColor: isOverdue ? '#FECDD3' : '#D4E8DC', borderRadius: 7, fontSize: '0.82rem', backgroundColor: fieldReadOnly ? '#f8f9fa' : undefined }} />
            {nextDueDate && <small style={{ color: '#1A6B4A', fontSize: '0.72rem', fontStyle: 'italic' }}>↳ After completion, next due auto-updates to {fmtDate(nextDueDate)}</small>}
          </FormGroup>
        </Col>
        <Col md={12}>
          <FormGroup>
            <Label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#4A7060' }}>Notes</Label>
            <Input type="textarea" bsSize="sm" rows={2} value={item.notes || ''} onChange={(e) => handleField('notes', e.target.value)} placeholder={fieldReadOnly ? '—' : 'Any notes about this inspection item...'} readOnly={fieldReadOnly}
              style={{ borderColor: '#D4E8DC', borderRadius: 7, fontSize: '0.82rem', resize: 'vertical', backgroundColor: fieldReadOnly ? '#f8f9fa' : undefined }} />
          </FormGroup>
        </Col>
      </Row>

      {/* Bottom section — completion / reminder / attachments */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginTop: 8, paddingTop: 12, borderTop: '1px solid #D4E8DC' }}>
        <div>
          <Label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#4A7060', display: 'block', marginBottom: 8 }}>Completion</Label>
          {/* ALL roles can toggle completion + add notes */}
          <CompletedToggle item={item} onChange={(updated) => onChange(index, updated)} readOnly={false} />
        </div>
        <div>
          <Label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#4A7060', display: 'block', marginBottom: 8 }}>Reminder</Label>
          {/* Reminder config is company-only */}
          {isCompanyRole
            ? <ReminderDaysInput item={item} onChange={(updated) => onChange(index, updated)} />
            : <span style={{ fontSize: '0.78rem', color: '#9BB5A8', fontStyle: 'italic' }}>{item.reminderDays ? `${item.reminderDays} days before due` : 'Not set'}</span>}
        </div>
        <div>
          <Label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#4A7060', display: 'block', marginBottom: 8 }}>Attachments</Label>
          {/* ALL roles can add/view attachments */}
          <AttachmentUploader
            itemIndex={index}
            existingAttachments={item.completed?.attachments || []}
            onAttachmentsChange={(keys) => onChange(index, { ...item, completed: { ...(item.completed || { completedDate: '', notes: '' }), attachments: keys } })}
            inspectionId={isEditMode ? inspectionId : null}
            isEditMode={isEditMode}
            readOnly={false}
          />
        </div>
      </div>
    </div>
  );
};

const InspectionTrackerFormModal = ({
  isOpen, onClose, onSave, inspectionData, isNew, propertyContext, typeOptions = [], frequencyOptions = [], userRole,
}) => {
  const [formData, setFormData] = useState(null);
  const [saving, setSaving] = useState(false);
  const pendingFilesByItemRef = useRef({});
  const selectStyles = buildSelectStyles();
  const isCompanyRole = userRole === 'company';

  useEffect(() => {
    if (isOpen) {
      if (inspectionData) {
        setFormData({ ...inspectionData });
      } else {
        setFormData({ property: { propertyId: propertyContext?.propertyId || '', buildingName: propertyContext?.buildingName || '' }, year: new Date().getFullYear(), inspections: [{ ...defaultInspectionItem }] });
      }
      pendingFilesByItemRef.current = {};
    }
  }, [isOpen, inspectionData, propertyContext]);

  const handleItemChange = useCallback((index, updatedItem) => {
    setFormData((prev) => { const items = [...(prev.inspections || [])]; items[index] = updatedItem; return { ...prev, inspections: items }; });
  }, []);

  const addItem = () => {
    if ((formData?.inspections || []).length >= MAX_ITEMS) return;
    setFormData((prev) => ({ ...prev, inspections: [...(prev.inspections || []), { ...defaultInspectionItem }] }));
  };

  const removeItem = (index) => {
    setFormData((prev) => { const items = [...(prev.inspections || [])]; items.splice(index, 1); return { ...prev, inspections: items }; });
    const np = { ...pendingFilesByItemRef.current }; delete np[index]; pendingFilesByItemRef.current = np;
  };

  const handleSave = async () => {
    if (!formData) return;
    if (!formData.year) { toast.error('Year is required'); return; }
    if (!formData.inspections?.length) { toast.error('At least one inspection item is required'); return; }

    // Company role: full validation. Other roles: skip field validation, only save completion changes.
    if (isCompanyRole) {
      const incomplete = formData.inspections.findIndex((i) => !i.inspectionTypeId || !i.frequencyId || !i.contractor?.trim() || !i.dueDate);
      if (incomplete >= 0) { toast.error(`Item ${incomplete + 1}: Inspection type, frequency, contractor, and due date are required`); return; }
    }

    const payload = { ...formData, property: { propertyId: propertyContext.propertyId, buildingName: propertyContext.buildingName } };
    setSaving(true);
    try {
      const result = await onSave(payload, isNew);
      if (isNew && result?._id) {
        const newId = result._id;
        for (const [idxStr, files] of Object.entries(pendingFilesByItemRef.current)) {
          if (!files?.length) continue;
          await uploadAttachmentFiles(newId, Number(idxStr), files, []);
        }
      }
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(`Failed to ${isNew ? 'create' : 'save'} inspection`);
    } finally {
      setSaving(false);
    }
  };

  const itemCount = formData?.inspections?.length || 0;
  const completedCount = (formData?.inspections || []).filter((i) => i.completed).length;
  const overdueCount = (formData?.inspections || []).filter((i) => !i.completed && i.dueDate && new Date(i.dueDate) < new Date()).length;

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="xl" className="it-form-modal" style={{ maxWidth: 980 }} scrollable>
      <div className="it-header" style={{ borderRadius: '8px 8px 0 0' }}>
        <div className="it-header__left">
          <div className="it-header__icon-wrap">
            <FeatherIcon icon={isNew ? 'plus-circle' : (isCompanyRole ? 'edit-2' : 'eye')} size={18} color="#A7F3D0" />
          </div>
          <div className="it-header__meta">
            <div className="it-header__label">{isNew ? 'New Inspection Record' : isCompanyRole ? 'Edit Inspection Record' : 'View Inspection Record'}</div>
            <div className="it-header__title">
              {propertyContext?.name || 'Property'}
              {propertyContext?.buildingName ? ` — ${propertyContext.buildingName}` : ''}
              {propertyContext?.type ? ` (${propertyContext.type})` : ''}
            </div>
          </div>
          <div className="it-header__badges">
            {itemCount > 0 && <span className="it-header__badge it-header__badge--records">{itemCount} item{itemCount !== 1 ? 's' : ''}</span>}
            {completedCount > 0 && <span className="it-header__badge" style={{ background: '#E8F5EE', color: '#1A6B4A' }}>{completedCount} completed</span>}
            {overdueCount > 0 && <span className="it-header__badge it-header__badge--overdue">{overdueCount} overdue</span>}
          </div>
        </div>
        <button type="button" className="it-icon-btn" onClick={onClose}><FeatherIcon icon="x" size={14} color="white" /></button>
      </div>

      <ModalBody style={{ padding: '20px 24px', maxHeight: '75vh', overflowY: 'auto' }}>
        {!isCompanyRole && !isNew && <ReadOnlyBanner />}

        <Row className="g-3 mb-4">
          <Col md={4}>
            <FormGroup>
              <Label style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1A6B4A' }}>Property</Label>
              <div className="form-control form-control-sm" style={{ backgroundColor: '#f8f9fa', color: '#495057', cursor: 'default', borderColor: '#D4E8DC', borderRadius: 8, fontSize: '0.82rem' }}>{propertyContext?.name || '—'}</div>
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup>
              <Label style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1A6B4A' }}>Building</Label>
              <div className="form-control form-control-sm" style={{ backgroundColor: '#f8f9fa', color: '#495057', cursor: 'default', borderColor: '#D4E8DC', borderRadius: 8, fontSize: '0.82rem' }}>{propertyContext?.buildingName || '—'}</div>
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup>
              <Label style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1A6B4A' }}>Year *</Label>
              {isCompanyRole ? (
                <Input type="number" bsSize="sm" value={formData?.year || ''} onChange={(e) => setFormData((prev) => ({ ...prev, year: Number(e.target.value) }))} min="2000" max="2100" placeholder="e.g. 2026" style={{ borderColor: '#D4E8DC', borderRadius: 8, fontSize: '0.82rem' }} />
              ) : (
                <div className="form-control form-control-sm" style={{ backgroundColor: '#f8f9fa', color: '#495057', cursor: 'default', borderColor: '#D4E8DC', borderRadius: 8, fontSize: '0.82rem' }}>{formData?.year || '—'}</div>
              )}
            </FormGroup>
          </Col>
        </Row>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <FeatherIcon icon="list" size={16} color="#1A6B4A" />
            <span style={{ fontWeight: 800, fontSize: '0.9rem', color: '#1A2D24' }}>Inspection Items ({itemCount}/{MAX_ITEMS})</span>
          </div>
          {isCompanyRole && (
            <button type="button" className="it-btn it-btn--add-item" onClick={addItem} disabled={itemCount >= MAX_ITEMS}>
              <FeatherIcon icon="plus" size={13} /> Add Item
            </button>
          )}
        </div>

        {(formData?.inspections || []).map((item, idx) => (
          <InspectionItemCard
            key={idx}
            item={item}
            index={idx}
            inspectionId={formData?._id || null}
            isEditMode={!isNew && !!formData?._id}
            typeOptions={typeOptions}
            frequencyOptions={frequencyOptions}
            onChange={handleItemChange}
            onRemove={removeItem}
            canRemove={itemCount > 1}
            isCompanyRole={isCompanyRole}
          />
        ))}

        {itemCount === 0 && (
          <div style={{ textAlign: 'center', padding: '32px 20px', color: '#6B8F7C' }}>
            <FeatherIcon icon="clipboard" size={32} color="#D4E8DC" />
            <div style={{ marginTop: 10, fontWeight: 600, fontSize: '0.9rem' }}>No inspection items yet</div>
            <div style={{ fontSize: '0.8rem', marginTop: 4 }}>Click "Add Item" to add inspection items</div>
          </div>
        )}
      </ModalBody>

      <ModalFooter style={{ borderTop: '1px solid #D4E8DC', padding: '12px 24px', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" className="it-btn it-btn--cancel" onClick={onClose} disabled={saving}>
          <FeatherIcon icon="x" size={13} /> {isCompanyRole ? 'Cancel' : 'Close'}
        </button>
        <button type="button" className={`it-btn it-btn--save${saving ? ' it-btn--saving' : ''}`} onClick={handleSave} disabled={saving}>
          {saving ? <Spinner size="sm" /> : <FeatherIcon icon="check" size={13} />}
          {saving ? 'Saving...' : isNew ? 'Create Inspection' : isCompanyRole ? 'Save Changes' : 'Save Completion'}
        </button>
      </ModalFooter>
    </Modal>
  );
};

export { InspectionTrackerFormModal };